import React, {useMemo, useCallback, useState, useRef, useEffect} from "react"
import _ from "lodash"
import {useAuth0} from "@auth0/auth0-react"
import {useDispatch, useSelector} from "react-redux"

// UI
import {Card} from "@uprise/card"
import {spacing} from "@uprise/spacing"
// Slices
import {authSelector} from "features/Auth/authSlice"
import {clearCoachesDataState, fetchCoachesCalendarData, coachDataSelector, fetchCoachesData} from "../coachesSlice"
// Components
import ToggleModal from "components/shared/Modal/ToggleModal"
import {Alerts} from "./Alerts"
// Modals
import CalendarList from "../Modals/CalendarList"
import DisableDialog from "../Modals/DisableDialog"
// Styles
import {HeaderText, ColumnContainer, ColumnIcon, ColumnText} from "shared/Table/index.style"
import {HeaderTableContent} from "../../Employers/index.style"
import {StyledImg} from "../../../shared/Form/index.style"
import {SearchInputContainer, SearchInputfigure, SearchInputImage} from "../../Employers/AllEmployers/index.style"
import {SearchInput} from "features/Coaches/AllCoaches/index.style"

//Assets
import LogoHeadIcon from "@uprise/icons/icons/png/logo-head/logo-head.png"
import IconSearch from "assets/images/icons/svg/search.svg"
import ComponentWrapper from "components/HOC/ComponentWrapper"
import TableComponent from "components/shared/Table"
import CrossIcon from "assets/images/icons/svg/cross.svg"
import TickIcon from "assets/images/icons/svg/Tick.svg"

const AllCoaches = ({history}) => {
	const dispatch = useDispatch()

	const [token, setToken] = useState(null)
	const [pageCount, setPageCount] = React.useState(0)
	const [validCals, setValidCals] = React.useState(0)
	const [coachSearchTermText, setCoachSearchTermText] = useState("")
	const [pageNo, setPageNo] = useState(0)

	// Selectors
	const {coachesCalendarApiState, coachStatusApiState, allCoachesApiState, bulkCancelDisableApiState} =
		useSelector(coachDataSelector)
	const authState = useSelector(authSelector)

	const {getAccessTokenSilently} = useAuth0()
	const fetchIdRef = useRef(0)
	const searchRef = useRef("")

	useEffect(() => {
		// Clear coach data state on unmount
		return () => dispatch(clearCoachesDataState())
	}, [])

	useEffect(() => {
		;(async () => {
			const token = await getAccessTokenSilently()
			setToken(token)
		})()
	}, [])

	useEffect(() => {
		setValidCals(coachesCalendarApiState.validCals)
	}, [coachesCalendarApiState])

	useEffect(() => {
		if (coachStatusApiState.isSuccess || bulkCancelDisableApiState.isSuccess) {
			dispatch(
				fetchCoachesData({
					limit: 20,
					skip: 0,
					searchTerm: "",
					token
				})
			)
		}
	}, [coachStatusApiState, bulkCancelDisableApiState])

	useEffect(() => {
		if (allCoachesApiState.totalCount > 0) {
			setPageCount(Math.ceil(allCoachesApiState.totalCount / 20))
		}
	}, [allCoachesApiState])

	const [filterByName, setFilterByName] = React.useState("")
	const filterFunction = item => item.name?.toLowerCase().indexOf(filterByName.toLowerCase()) !== -1

	const fetchData = useCallback(
		({pageSize, pageIndex}) => {
			// This will get called when the table needs new data
			// You could fetch your data from literally anywhere,
			// even a server. But for this example, we'll just fake it.

			// Give this fetch an ID
			const fetchId = ++fetchIdRef.current

			if (searchRef.current !== coachSearchTermText) {
				pageIndex = 0
			}

			setPageNo(pageIndex)

			if (fetchId === fetchIdRef.current && token && authState.data && authState.data.id) {
				dispatch(
					fetchCoachesData({
						limit: pageSize,
						skip: pageSize * pageIndex,
						searchTerm: coachSearchTermText,
						token
					})
				)

				dispatch(fetchCoachesCalendarData({token}))
			}

			searchRef.current = coachSearchTermText
		},
		[token, authState, coachSearchTermText]
	)

	const _handleSearchCoachNameChange = useCallback(
		_.debounce(
			value => {
				setCoachSearchTermText(value)
			},
			1500,
			{maxWait: 1500}
		),
		[]
	)

	const columns = useMemo(
		() => [
			{
				Header: () => <HeaderText alignText='left'>Coach name</HeaderText>,
				accessor: "name",
				show: true,
				desc: false,
				sortType: "string",
				Cell: cellInfo => {
					const {
						cell: {
							row: {
								original: {name, id, data}
							}
						}
					} = cellInfo
					return (
						<ColumnContainer align='flex-start'>
							<StyledImg
								width='30px'
								height='30px'
								src={data?.photos?.imageURL ? data.photos.imageURL : LogoHeadIcon}
								alt='user-icon'
								noMargin
							/>
							<ColumnText
								onClick={() => {
									history.push(`/coaches/${id}`)
								}}
								id={cellInfo.row.original.id}
								row={cellInfo.row.original}
							>
								{name}
							</ColumnText>
						</ColumnContainer>
					)
				}
			},
			{
				Header: "Coach ID",
				accessor: "id",
				show: true,
				Cell: ({
					cell: {
						row: {
							original: {id}
						}
					}
				}) => {
					return (
						<ColumnContainer align='center'>
							<ColumnText>{`#${id}`}</ColumnText>
						</ColumnContainer>
					)
				}
			},
			{
				Header: "Location",
				//accessor: "data.location",
				accessor: c => {
					return c.data?.location?.toLowerCase() || ""
				},
				show: true,
				sortType: "string",
				Cell: cellInfo => {
					let location = cellInfo.cell.row.original.data?.location
						? cellInfo.cell.row.original.data.location
						: ""

					return (
						<ColumnContainer align='center'>
							<ColumnText>{location}</ColumnText>
						</ColumnContainer>
					)
				}
			},
			{
				Header: "Time Zone",
				accessor: "data.timezone",
				show: true,
				Cell: cellInfo => {
					return (
						<ColumnContainer align='center'>
							<ColumnText>{cellInfo.cell.row.original.data.timezone}</ColumnText>
						</ColumnContainer>
					)
				}
			},
			{
				Header: "Email",
				accessor: "email",
				show: true,
				style: {overflow: "auto"},
				Cell: cellInfo => {
					return (
						<ColumnContainer align='center'>
							<ColumnText>{cellInfo.cell.row.original.email}</ColumnText>
						</ColumnContainer>
					)
				}
			},
			{
				Header: "Phone",
				accessor: c => {
					const phNumber = c?.data?.phoneNumber?.replace("+", "")
					return phNumber || ""
				},
				sortType: "number",
				show: true,
				Cell: cellInfo => {
					return (
						<ColumnContainer align='center'>
							<ColumnText>{cellInfo.cell.row.original.data.phoneNumber}</ColumnText>
						</ColumnContainer>
					)
				}
			},
			{
				Header: "Status",
				accessor: c => {
					return c.disabled ? 1 : 0
				},
				sortType: "number",
				show: true,
				Cell: ({
					cell: {
						row: {
							original: {id, disabled}
						}
					}
				}) => {
					return <DisableDialog disabled={disabled} id={id} />
				}
			},
			{
				Header: "Calendar API Status",
				// accessor: "calendarStatus",
				// disableSortBy: true,
				accessor: c => {
					return validCals?.[c?.data?.calendarId] === "owner" || validCals?.[c?.data?.calendarId] === "writer"
						? 1
						: 0
				},
				sortType: "number",
				Cell: x => {
					const {
						cell: {
							row: {
								original: {
									data: {calendarId, extraCalendars}
								}
							}
						}
					} = x
					console.log(calendarId, "calendarId calendarId")
					return (
						<>
							<ToggleModal
								toggle={show => (
									<ColumnContainer align='center' onClick={show} aria-hidden='true'>
										{validCals?.[calendarId] === "owner" || validCals?.[calendarId] === "writer" ? (
											<ColumnIcon>
												<img src={TickIcon} alt={"available"} data-testid='apiStatus' />
											</ColumnIcon>
										) : (
											<ColumnIcon>
												<img src={CrossIcon} alt={"Not available"} data-testid='apiStatus' />
											</ColumnIcon>
										)}
									</ColumnContainer>
								)}
								content={hide => (
									<CalendarList
										hide={hide}
										calendarId={calendarId}
										extraCalendars={extraCalendars}
										validCals={validCals}
									/>
								)}
							/>
						</>
					)
				}
			}
		],
		[dispatch, getAccessTokenSilently, validCals]
	)

	return (
		<ComponentWrapper
			menuActiveIndex={7}
			subMenuActiveIndex={12}
			headerTitle='All Coaches'
			headerRightAlignContent={
				<HeaderTableContent>
					<SearchInputContainer>
						<SearchInputfigure>
							<SearchInputImage src={IconSearch} />
						</SearchInputfigure>
						<SearchInput
							type={"text"}
							data-testid='searchByCoachName'
							label={"email"}
							placeholder={"Search Coach Name or Email"}
							isRequired={false}
							onChange={e => _handleSearchCoachNameChange(e.target.value)}
						/>
					</SearchInputContainer>
				</HeaderTableContent>
			}
		>
			<Alerts
				coachStatusApiState={coachStatusApiState}
				allCoachesApiState={allCoachesApiState}
				coachesCalendarApiState={coachesCalendarApiState}
			/>
			<Card padding={spacing.s10} backgroundColor='white' data-testid='coachData'>
				<TableComponent
					pageCount={pageCount}
					fetchData={fetchData}
					pageNo={pageNo}
					columns={columns}
					loading={allCoachesApiState.isFetching || !allCoachesApiState.isSuccess}
					data={Object.values(allCoachesApiState.coaches)}
				/>
			</Card>
		</ComponentWrapper>
	)
}

export default AllCoaches
